import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/config";
import {
  House, ClockClockwise, CalendarCheck, Briefcase, Airplane, ChartBar,
  UsersThree, FolderOpen, FileText, ChartPieSlice, CalendarBlank, Gear, Scroll, SignOut, List,
  Money, Receipt, UserCircle, Bank, Coins, TreeStructure, IdentificationBadge, UsersFour, CheckCircle, Timer
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Avatar } from "@/components/Shared";
import NotificationBell from "@/components/NotificationBell";

const EMP_NAV = [
  { to: "/", label: "Dashboard", icon: House },
  { to: "/timesheet", label: "My Timesheet", icon: ClockClockwise },
  { to: "/attendance", label: "My Attendance", icon: CalendarCheck },
  { to: "/projects", label: "My Projects", icon: Briefcase },
  { to: "/leave", label: "My Leave", icon: Airplane },
  { to: "/salary", label: "My Salary", icon: Money },
  { to: "/payslips", label: "Salary Slips", icon: Receipt },
  { to: "/profile", label: "My Profile", icon: UserCircle },
];

// Team-level Admin: only their team's sections
const ADMIN_NAV = [
  { to: "/", label: "Dashboard", icon: House },
  { to: "/admin/employees", label: "My Team", icon: UsersThree },
  { to: "/admin/approvals", label: "Pending Approvals", icon: CheckCircle },
  { to: "/admin/leave", label: "Leave History", icon: Airplane },
  { to: "/admin/timesheets", label: "Timesheet History", icon: FileText },
  { to: "/admin/projects", label: "Project Assignments", icon: FolderOpen },
  { to: "/admin/attendance", label: "Attendance", icon: CalendarCheck },
  { to: "/timesheet", label: "My Timesheet", icon: Timer },
  { to: "/admin/my-attendance", label: "My Attendance", icon: ClockClockwise },
  { to: "/admin/activity", label: "Daily Activity", icon: ChartBar },
  { to: "/admin/reports", label: "Team Reports", icon: ChartPieSlice },
  { to: "/admin/hierarchy", label: "My Org", icon: TreeStructure },
  { to: "/admin/directory", label: "Directory", icon: IdentificationBadge },
  { to: "/profile", label: "My Profile", icon: UserCircle },
];

// Super Admin: full control panel
const SUPER_NAV = [
  { to: "/", label: "Dashboard", icon: House },
  { to: "/admin/approvals", label: "Pending Approvals", icon: CheckCircle },
  { to: "/admin/employees", label: "Employees", icon: UsersThree },
  { to: "/admin/hierarchy", label: "Organization", icon: TreeStructure },
  { to: "/admin/directory", label: "Directory", icon: IdentificationBadge },
  { to: "/admin/teams", label: "Departments & Teams", icon: UsersFour },
  { to: "/admin/projects", label: "Projects", icon: FolderOpen },
  { to: "/admin/leave", label: "Leave History", icon: Airplane },
  { to: "/admin/timesheets", label: "Timesheet History", icon: FileText },
  { to: "/admin/activity", label: "Daily Activity", icon: ChartBar },
  { to: "/admin/attendance", label: "Attendance", icon: CalendarCheck },
  { to: "/admin/payroll", label: "Payroll", icon: Money },
  { to: "/admin/salary-structures", label: "Salary Structures", icon: Coins },
  { to: "/admin/salary-components", label: "Salary Components", icon: Receipt },
  { to: "/admin/loans", label: "Loans & Advances", icon: Bank },
  { to: "/admin/reports", label: "Reports", icon: ChartPieSlice },
  { to: "/admin/holidays", label: "Holidays", icon: CalendarBlank },
  { to: "/admin/settings", label: "Settings", icon: Gear },
  { to: "/admin/audit", label: "Audit Logs", icon: Scroll },
];

const ROLE_LABEL = { super_admin: "Super Admin", admin: "Team Admin", employee: "Member" };
const ROLE_BADGE = {
  super_admin: "bg-[#FF5500] text-white",
  admin: "bg-[#1F4E8C] text-white",
  employee: "bg-neutral-200 text-neutral-700",
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const company = useCompany();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const role = user?.role || "employee";
  const nav = role === "super_admin" ? SUPER_NAV : role === "admin" ? ADMIN_NAV : EMP_NAV;
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (role !== "super_admin" && role !== "admin") return;
    let active = true;
    const load = async () => {
      try {
        const r = await api.get("/approvals/pending-count");
        if (!active) return;
        const total = r.data?.total || 0;
        setPendingCount(total);
        const base = document.title.replace(/^\(\d+\)\s*/, "");
        document.title = total > 0 ? `(${total}) ${base}` : base;
      } catch (_) {}
    };
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("approvals-changed", load);
    return () => { active = false; clearInterval(t); window.removeEventListener("approvals-changed", load); };
  }, [role]);

  return (
    <div className="min-h-screen flex bg-[#F7F7F5]">
      {/* Sidebar */}
      <aside className={`${open ? "block fixed inset-0 z-40" : "hidden"} lg:block lg:relative lg:w-64 text-white flex-shrink-0`}
             style={{ backgroundColor: company.brand.secondaryColor }}>
        <div className="h-16 flex items-center gap-3 px-6 border-b border-white/10">
          <img src={company.brand.logo} alt={company.shortName} className="h-9 w-9 object-contain bg-white p-0.5" />
          <div className="font-head font-bold text-lg tracking-tighter">{company.shortName}</div>
          <div className="ml-auto eyebrow text-sky-200/70 text-[10px]" data-testid="sidebar-role">{ROLE_LABEL[role]}</div>
        </div>
        <nav className="py-4 overflow-y-auto h-[calc(100vh-4rem)]">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === "/"} onClick={() => setOpen(false)}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              style={({ isActive }) => isActive ? { backgroundColor: company.brand.primaryColor } : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm transition-colors duration-200 ${
                  isActive ? "text-white font-semibold" : "text-sky-100/90 hover:bg-white/10 hover:text-white"
                }`
              }>
              <Icon size={18} weight="regular" />
              {label}
              {label === "Pending Approvals" && pendingCount > 0 && (
                <span data-testid="pending-approvals-badge"
                  className="ml-auto min-w-[20px] h-5 px-1.5 bg-[#FF5500] text-white text-[11px] font-bold grid place-items-center rounded-full">
                  {pendingCount > 99 ? "99+" : pendingCount}
                </span>
              )}
            </NavLink>
          ))}
          <button onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="logout-button"
            className="flex items-center gap-3 px-6 py-3 text-sm text-sky-100/90 hover:bg-white/10 hover:text-white w-full text-left mt-4">
            <SignOut size={18} /> Sign Out
          </button>
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-[#E5E5E5] flex items-center justify-between px-4 lg:px-8">
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="menu-toggle">
            <List size={22} />
          </button>
          <div className="hidden lg:flex items-center gap-3">
            <div>
              <div className="eyebrow">Welcome back</div>
              <div className="font-head font-semibold text-base" data-testid="header-user-name">{user?.name}</div>
            </div>
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 ${ROLE_BADGE[role]}`} data-testid="role-badge">
              {ROLE_LABEL[role]}
            </span>
            {user?.department && (
              <span className="text-[10px] font-medium px-2 py-1 bg-neutral-100 text-neutral-600" data-testid="dept-badge">{user.department}</span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <div className="text-right hidden sm:block">
              <div className="text-xs text-neutral-500">{user?.designation || ROLE_LABEL[role]}</div>
              <div className="text-sm font-medium">{user?.email}</div>
            </div>
            <div className="h-10 w-10 overflow-hidden">
              <Avatar name={user?.name} photo={user?.profile_photo} size={40} />
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 overflow-x-auto">{children}</main>
      </div>
    </div>
  );
}
